import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { ToastContainer } from "react-toastify";
import ReactBSAlert from "react-bootstrap-sweetalert";

import SimpleHeader from "@/components/Headers/SimpleHeader";
import {Button,Card,CardHeader,CardBody,Container,Row,Col,FormGroup,Media} from "reactstrap";

import { getAds, deleteAd } from "@/services/adServices/adActions";
import { getProductById } from "@/services/productServices/productActions";

import AddAdsContainer from "./AddAdsContainer";
import UpdateAdContainer, { type AdRow } from "./UpdateAdContainer";
import TableWrapper from "@/utils/TableWrapper";
import NotificationAlertOptions from "@/layouts/Alerts";

const DisplayAds = () => {
  const dispatch = useAppDispatch();
  const adsState = useAppSelector((s) => s.ad);
  const permission = useCurrentPermission();
  const [showUpdate, setShowUpdate] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [alert, setAlert] = useState<ReactNode>(null);
  const [ads, setAds] = useState<AdRow[]>([]);
  const [adToUpdate, setAdToUpdate] = useState<AdRow>({});

  useEffect(() => {
    dispatch(getAds());
  }, [dispatch]);

  useEffect(() => {
    if (adsState?.ads) setAds(adsState.ads);
  }, [adsState]);

  const showUpdateHandler = (_e, row) => {
    setShowUpdate(true);
    setShowAdd(false);
    setAdToUpdate(row);
  };

  const hideUpdateHandler = () => setShowUpdate(false);
  const showAddHandler = (e) => {
    e.preventDefault();
    setShowAdd(true);
    setShowUpdate(false);
  };
  const hideAddHandler = (e) => {
    e.preventDefault();
    setShowAdd(false);
    setShowUpdate(false);
  };
  const hideAlert = () => setAlert(null);
  const questionAlert = (_e, data) => {
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert prop-types
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to remove this Ad?"
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          // Confirm only after the delete resolved successfully; a failure is
          // already reported through the errors slice (SCRUM-1944).
          const deleted = await dispatch(deleteAd(data._id));
          hideAlert();
          if (deleted) NotificationAlertOptions("success", "Ad Deleted with success");
        }}
        onCancel={hideAlert}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        Delete Operation
      </ReactBSAlert>
    );
  };

  const columns = useMemo(() => {
    const base: any[] = [
      {
        accessorKey: "adsMedia",
        header: "Image",
        cell: ({ row }) => (
          <div key={row.original._id}>
            <Media className="align-items-center">
              <img
                className="avatar rounded-circle mr-3"
                src={row.original.adsMedia}
                alt="Ad Media"
              />
            </Media>
          </div>
        ),
      },
      { accessorKey: "adsTitle", header: "Title" },
      {
        header: "Description",
        accessorFn: (row) => (row.adsDescription || "").substr(0, 50),
      },
      { accessorKey: "adsPosition", header: "Position" },
      {
        accessorFn: (row) => row.label?.[0]?.value?.label,
        header: "Label",
      },
    ];

    if (permission?.edit || permission?.publish) {
      base.push({
        header: "ACTION",
        cell: ({ row }) => (
          <div key={row.original._id}>
            <Button
              color="primary"
              size="sm"
              onClick={(e) => showUpdateHandler(e, row.original)}
            >
              <span className="btn-inner--icon mr-1">
                <i className="ni ni-settings-gear-65" />
              </span>
              Update
            </Button>
            <Button
              className="btn-round btn-icon"
              color="danger"
              id="tooltip443412080"
              onClick={(e) => questionAlert(e, row.original)}
              size="sm"
            >
              <span className="btn-inner--icon mr-1">
                <i className="fas fa-trash" />
              </span>
              <span className="btn-inner--text">Delete</span>
            </Button>
          </div>
        ),
      });
    }

    return base;
  }, [permission]);

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      {alert}
      <SimpleHeader name="Display Ads" parentName="Ads" />
      <Container className="mt--6" fluid>
        <Row>
          <Col md={showUpdate || showAdd ? 8 : 12}>
            <Card>
              <CardHeader>
                <h3 className="mb-0">Manage Ads</h3>
              </CardHeader>
              <CardBody>
                {permission?.edit || permission?.publish ? (
                  <Row className="ml-3">
                    <FormGroup className="row">
                      <Button color="primary" size="sm" onClick={showAddHandler}>
                        Add New
                      </Button>
                    </FormGroup>
                  </Row>
                ) : null}

                <TableWrapper data={ads} columns={columns} />
              </CardBody>
            </Card>
          </Col>

          {(showUpdate || showAdd) && (
            <Col xl="4">
              {showUpdate && (
                <Card>
                  <CardHeader>
                    <Row className="align-items-center">
                      <Col xs="8">
                        <h5 className="h3 mb-0">Update Ad</h5>
                      </Col>
                      <Col className="text-right" xs="4">
                        <Button color="danger" size="sm" onClick={hideAddHandler}>
                          close
                        </Button>
                      </Col>
                    </Row>
                  </CardHeader>
                  <UpdateAdContainer ad={adToUpdate} />
                </Card>
              )}

              {showAdd && (
                <Card>
                  <CardHeader>
                    <Row className="align-items-center">
                      <Col xs="8">
                        <h5 className="h3 mb-0">Add new Ad</h5>
                      </Col>
                      <Col className="text-right" xs="4">
                        <Button color="danger" size="sm" onClick={hideAddHandler}>
                          close
                        </Button>
                      </Col>
                    </Row>
                  </CardHeader>
                  <AddAdsContainer />
                </Card>
              )}
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default DisplayAds;
